import { Container, Row, Col, Card, Button, Nav } from "react-bootstrap";
import { Line } from "react-chartjs-2";
import cloud from '../../../assets/images/cloud.png';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { useNavigate } from "react-router-dom";
import { useEffect, useState, useContext, useRef } from "react";
import Context from "../../../context/Context";
import { HiOutlineBuildingOffice2 } from "react-icons/hi2";
import { FiFileText } from "react-icons/fi";
import { getAdminDashboardStats, getChangeRequests, getOnboardingRequests } from "../../../api/admin/adminApi";
import { timeAgo } from "../../../utils/helper";
import InfiniteScroll from "../../common/infiniteScroll/InfiniteScroll";
import { PiBuildings } from "react-icons/pi";
import { BiCalendar, BiDollar } from 'react-icons/bi';
import { MdOutlineCategory } from "react-icons/md";



ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

const AdminDashboard = () => {
    const [activeKey, setActiveKey] = useState("onboarding");
    const [stats, setStats] = useState({});
    const [requests, setRequests] = useState([]);
    const [requestsCount, setRequestsCount] = useState(null);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const loaderRef = useRef(null);
    const navigate = useNavigate();
    const { adminDashboardTab, setAdminDashboardTab, setPageTitle } = useContext(Context);
    const [timeframe, setTimeframe] = useState('monthly');
    const [chartData, setChartData] = useState({});


    const monthlyData = {
        labels: [
            "Jan", "Feb", "Mar", "Apr", "May", "Jun",
            "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        ],
        datasets: [
            {
                label: "Basic Plan",
                data: [5, 15, 40, 60, 80, 100, 20, 50, 40, 55, 70, 65],
                borderColor: "#0070E0",
                backgroundColor: "#0070E0",
                borderWidth: 2,
                pointRadius: 3,
                tension: 0.4,
            },
            {
                label: "Premium Plan",
                data: [10, 50, 120, 110, 180, 200, 90, 70, 60, 90, 110, 130],
                borderColor: "#FFA526",
                backgroundColor: "#FFA526",
                borderWidth: 2,
                pointRadius: 3,
                tension: 0.4,
            },
        ],
    };

    const yearlyData = {
        labels: [
            "2021", "2022", "2023", "2024", "2025"
        ],
        datasets: [
            {
                label: "Basic Plan",
                data: [50, 60, 70, 80, 90],
                borderColor: "#0070E0",
                backgroundColor: "#0070E0",
                borderWidth: 2,
                pointRadius: 3,
                tension: 0.4,
            },
            {
                label: "Premium Plan",
                data: [100, 120, 140, 160, 180],
                borderColor: "#FFA526",
                backgroundColor: "#FFA526",
                borderWidth: 2,
                pointRadius: 3,
                tension: 0.4,
            },
        ],
    };

    useEffect(() => {
        if (timeframe === 'monthly') {
            setChartData(monthlyData);
        } else {
            setChartData(yearlyData);
        }
    }, [timeframe]);

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: "bottom",
                labels: { usePointStyle: false, boxWidth: 8 },
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: { stepSize: 25 },
                grid: { color: "#f0f0f0" },
            },
            x: {
                grid: { display: false },
            },
        },
    };

    const handleViewAll = () => {
        if (activeKey === "onboarding") {
            navigate("/admin/onboarding-requests");
        } else {
            navigate("/admin/change-requests");
        }
    };

    useEffect(() => {
        setPageTitle('Dashboard');
        setLoading(true);
        let mode = "";
        if (adminDashboardTab === 'Enterprise') {
            mode = "Managed";
        } else if (adminDashboardTab === 'Business') {
            mode = "Self Serve";
        } else if (adminDashboardTab === 'Personal') {
            mode = "Personal";
        }

        getAdminDashboardStats({ mode }, (res) => {
            if (res.status === true) {
                setStats(res.data);
            }
            setLoading(false);
        });

    }, [adminDashboardTab, setPageTitle])

    const handleRequests = (pageNum = 1) => {
        if (adminDashboardTab === 'Enterprise') {
            setLoading(true);
            const exactRqstFunc = activeKey === 'onboarding' ? getOnboardingRequests : getChangeRequests;
            exactRqstFunc({ page: pageNum }, (res) => {
                if (res.status === true) {
                    setRequests(prev => pageNum === 1 ? (res?.data || []) : [...prev, ...(res?.data || [])]);
                    setRequestsCount(res?.count);
                }
                setLoading(false);
            });
        }
    }

    useEffect(() => {
        setPage(1);
        handleRequests(1);
    }, [activeKey, adminDashboardTab])

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
            </div>
        );
    }

    return (
        <Container fluid className="p-4 bg-light">
            <div className="d-flex items-center justify-content-between mb-3 flex-wrap">
                <h3 className="font-semibold fs-4">{`${adminDashboardTab} Mode`}</h3>
                <Nav
                    variant="tabs"
                    activeKey={adminDashboardTab}
                    onSelect={(k) => setAdminDashboardTab(k)}
                    className="mb-2 flex items-center custom-nav"
                >
                    <Nav.Item className="flex-fill text-left">
                        <Nav.Link eventKey="Enterprise" className="!text-xs">
                            Enterprise
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item className="flex-fill text-left">
                        <Nav.Link eventKey="Business" className="!text-xs">
                            Business
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item className="flex-fill text-left">
                        <Nav.Link eventKey="Personal" className="!text-xs">
                            Personal
                        </Nav.Link>
                    </Nav.Item>
                </Nav>
            </div>
            <Row>
                <Col xl={adminDashboardTab === 'Enterprise' ? 8 : 12} lg={12}>
                    <Row>
                        <Col lg={adminDashboardTab === 'Enterprise' ? 6 : 4} md={6} sm={12}>
                            <Card className="shadow-sm border-0 p-3 mb-3">
                                <div className="d-flex align-items-center justify-content-between">
                                    <h6 className="text-muted fs-16">{adminDashboardTab === 'Personal' ? 'All Registered Users' : 'Registered Companies'}</h6>
                                    <span style={{ backgroundColor: '#8686860F' }} className="p-2 rounded-circle">
                                        <HiOutlineBuildingOffice2 size={25} />
                                    </span>
                                </div>
                                <div className="d-flex align-items-center gap-4">
                                    <h3 className="fw-bold fs-1 text-primary mb-0">{adminDashboardTab === 'Personal' ? stats?.all_registered_users || 0 : stats?.total_registered_companies || 0}</h3>
                                </div>
                            </Card>
                        </Col>

                        <Col lg={adminDashboardTab === 'Enterprise' ? 6 : 4} md={6} sm={12}>
                            <Card className="shadow-sm border-0 p-3 mb-3">
                                <div className="d-flex align-items-center justify-content-between">
                                    <h6 className="text-muted fs-16">Sheets Created</h6>
                                    <span style={{ backgroundColor: '#8686860F' }} className="p-2 rounded-circle">
                                        <FiFileText size={25} />
                                    </span>
                                </div>
                                <div className="d-flex align-items-center gap-4">
                                    <h3 className="fw-bold fs-1 text-primary mb-0">{stats?.number_of_sheets || 0}</h3>

                                </div>
                            </Card>
                        </Col>

                        <Col lg={adminDashboardTab === 'Enterprise' ? 6 : 4} md={6} sm={12}>
                            <Card className="shadow-sm border-0 p-3 mb-3">
                                <div className="d-flex align-items-center justify-content-between">
                                    <h6 className="text-muted fs-16">Total Units</h6>
                                    <span style={{ backgroundColor: '#8686860F' }} className="p-2 rounded-circle">
                                        <BiDollar size={25} />
                                    </span>
                                </div>
                                <div className="d-flex align-items-center gap-4">
                                    <h3 className="fw-bold fs-1 text-primary mb-0">{stats?.total_units || 0}</h3>

                                </div>
                            </Card>
                        </Col>

                        <Col lg={adminDashboardTab === 'Enterprise' ? 6 : 4} md={6} sm={12}>
                            <Card className="shadow-sm border-0 p-3 mb-3">
                                <div className="d-flex align-items-center justify-content-between">
                                    <h6 className="text-muted fs-16">Sheet Companies</h6>
                                    <span style={{ backgroundColor: '#8686860F' }} className="p-2 rounded-circle">
                                        <PiBuildings size={25} />
                                    </span>
                                </div>
                                <div className="d-flex align-items-center gap-4">
                                    <h3 className="fw-bold fs-1 text-primary mb-0">{stats?.total_sheet_companies || 0}</h3>

                                </div>
                            </Card>
                        </Col>

                        <Col lg={adminDashboardTab === 'Enterprise' ? 6 : 4} md={6} sm={12}>
                            <Card className="shadow-sm border-0 p-3 mb-3">
                                <div className="d-flex align-items-center justify-content-between">
                                    <h6 className="text-muted fs-16">Total Categories</h6>
                                    <span style={{ backgroundColor: '#8686860F' }} className="p-2 rounded-circle">
                                        <MdOutlineCategory size={25} />
                                    </span>
                                </div>
                                <div className="d-flex align-items-center gap-4">
                                    <h3 className="fw-bold fs-1 text-primary mb-0">{stats?.total_categories || 0}</h3>
                                </div>
                            </Card>
                        </Col>

                        <Col lg={adminDashboardTab === 'Enterprise' ? 6 : 4} md={6} sm={12}>
                            <Card className="shadow-sm border-0 p-3 mb-3">
                                <div className="d-flex align-items-center justify-content-between">
                                    <h6 className="text-muted fs-16">Total Timeframes</h6>
                                    <span style={{ backgroundColor: '#8686860F' }} className="p-2 rounded-circle">
                                        <BiCalendar size={25} />
                                    </span>
                                </div>
                                <div className="d-flex align-items-center gap-4">
                                    <h3 className="fw-bold fs-1 text-primary mb-0">{stats?.total_timeframes || 0}</h3>
                                </div>
                            </Card>
                        </Col>
                    </Row>
                    <Card className="shadow-sm border-0 p-3 pb-2 h-auto">
                        <div className="d-flex justify-content-between align-items-center mb-2 mt-1 flex-wrap">
                            <h6 className="mb-0 text-muted fs-16">Plan Users Using</h6>
                            <Nav variant="pills" activeKey={timeframe} onSelect={(k) => setTimeframe(k)} className="mb-2 custom-nav py-0"
                                style={{ width: 'auto', minWidth: '220px' }}>
                                <Nav.Item className="flex-fill text-center">
                                    <Nav.Link eventKey="monthly" className="fs-13 border me-2 px-3 py-1">
                                        Monthly
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item className="flex-fill text-center">
                                    <Nav.Link eventKey="yearly" className="fs-13 border px-3 py-1">
                                        Yearly
                                    </Nav.Link>
                                </Nav.Item>
                            </Nav>
                        </div>
                        <div className="flex-grow-1" style={{ height: '400px' }}>
                            {chartData.labels && chartData.labels.length > 0 ? (
                                <Line data={chartData} options={options} />
                            ) : (
                                <div className="text-center text-muted py-3">
                                    No data available
                                </div>
                            )}
                        </div>
                    </Card>
                </Col>

                {adminDashboardTab === 'Enterprise' && <Col xl={4} lg={12} className="mt-lg-4 mt-xl-0">
                    <Card className="shadow-sm border-0 p-3 h-100">
                        <div className="d-flex justify-content-between align-items-center mb-2 py-2 px-2"
                            style={{ backgroundColor: "#F5F5F5" }}>
                            <h6 className="mb-0 text-muted fs-16">Requests</h6>
                            <Button variant="link" size="sm" className="p-0 fw-bold fs-13 text-dark"
                                onClick={handleViewAll}
                            >
                                View All
                            </Button>
                        </div>
                        <Nav
                            variant="tabs"
                            activeKey={activeKey}
                            onSelect={(k) => setActiveKey(k)}
                            className="mb-2 flex items-center justify-between custom-nav"
                        >
                            <Nav.Item className="flex-fill text-left">
                                <Nav.Link eventKey="onboarding" className="!text-xs">
                                    Onboarding Requests
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item className="flex-fill text-left">
                                <Nav.Link eventKey="change" className="!text-xs">
                                    Change Requests
                                </Nav.Link>
                            </Nav.Item>
                        </Nav>
                        <div className="flex-grow-1" style={{ maxHeight: "calc(100vh - 20px)", overflowY: "auto" }}>
                            {requests?.length > 0 ? (
                                <InfiniteScroll
                                    onLoadMore={handleRequests}
                                    page={page}
                                    setPage={setPage}
                                    list={requests}
                                    listTotalCount={requestsCount}
                                    loading={loading}
                                    loaderRef={loaderRef}
                                >
                                    {requests.map((req, i) => (
                                        <div
                                            key={i}
                                            className="d-flex align-items-center border-bottom py-2"
                                        >
                                            <img
                                                src={cloud}
                                                alt="logo"
                                                className="me-2 rounded-circle"
                                            />
                                            <div>
                                                <strong className="fs-13">{req?.company_name}</strong>
                                                <div className="text-muted small fs-13">
                                                    {(
                                                        req?.hierarchy_data?.company_head
                                                            ? `${req?.hierarchy_data?.company_head?.first_name || ""} ${req?.hierarchy_data?.company_head?.last_name || ""}`
                                                            : `${req?.change_data?.company_head?.first_name || ""} ${req?.change_data?.company_head?.last_name || ""}`
                                                    ).trim()}
                                                </div>
                                            </div>
                                            <small className="ms-auto text-muted fs-16">{timeAgo(req?.created_at)}</small>
                                        </div>
                                    ))}
                                </InfiniteScroll>
                            ) : (
                                !loading && (
                                    <div className="text-center text-muted py-3">
                                        No requests available currently
                                    </div>
                                )
                            )}
                        </div>

                    </Card>
                </Col>}
            </Row>
        </Container>
    );
};

export default AdminDashboard;